// CustomerInvoiceImageModal.jsx
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "react-toastify";
import MyContext from "../../../MyContext";

const safeNumber = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
};

const computeInvoiceTotals = (form) => {
  const items = form.items || [];
  let subtotal = 0;
  let totalQty = 0;

  items.forEach((it) => {
    const q = safeNumber(it.quantity);
    const p = safeNumber(it.item_price);
    const lineTotal =
      it.total_price !== "" && !Number.isNaN(safeNumber(it.total_price))
        ? safeNumber(it.total_price)
        : q * p;

    subtotal += lineTotal;
    totalQty += q;
  });

  const received = safeNumber(form.Received);
  const balance = subtotal - received;

  return {
    subtotal,
    totalQty,
    received,
    balance,
  };
};

export default function CustomerInvoiceImageModal({ open, onClose, customer }) {
  const { config } = useContext(MyContext);
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    user_name: "",
    phone_no: "",
    order_id: "",
    order_date: today,
    orderType: "",
    items: [
      { product_id: "", quantity: "1", item_price: "", total_price: "" }
    ],
    Received: "",
    totalinwords: "",
  });

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState("");

  // Reset / prefill when opened
  useEffect(() => {
    if (open && customer) {
      setForm({
        user_name: customer?.customerName || "",
        phone_no: (customer?.customerMobile || "").replace(/\D/g, "").slice(-10),
        order_id: "",
        order_date: today,
        orderType: "",
        items: [
          { product_id: "", quantity: "1", item_price: "", total_price: "" }
        ],
        Received: "",
        totalinwords: "",
      });
      setPreview("");
    }
  }, [open, customer, today]);

  const handleFieldChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      const row = { ...items[index], [field]: value };

      const qty = safeNumber(field === "quantity" ? value : row.quantity);
      const price = safeNumber(field === "item_price" ? value : row.item_price);

      if (field === "quantity" || field === "item_price") {
        const lineTotal = qty * price;
        row.total_price = lineTotal ? lineTotal.toFixed(2) : "";
      }

      items[index] = row;
      return {
        ...prev,
        items,
      };
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { product_id: "", quantity: "1", item_price: "", total_price: "" }
      ],
    }));
  };

  const removeItem = (index) => {
    setForm((prev) => {
      if (prev.items.length === 1) return prev;
      const items = prev.items.slice();
      items.splice(index, 1);
      return { ...prev, items };
    });
  };

  const generateInvoiceImage = (payload) =>
    axios.post(
      "https://bumppy.in/anlook/api/v1/generateInvoiceImage.php",
      payload,
      config
    );

  const handleGenerate = async () => {
    if (!(form.user_name || "").trim()) return toast.warning("Enter customer name");
    if (!(form.phone_no || "").trim()) return toast.warning("Enter customer mobile");

    const mobile10 = form.phone_no.replace(/^91/, "").replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(mobile10)) {
      return toast.warning("Enter valid 10-digit Indian mobile");
    }

    const { subtotal, totalQty, received, balance } = computeInvoiceTotals(form);

    const payload = {
      customerDetails: {
        logo: "https://bumppy.in/anlook/api/v1/uploads/logos/chaafo_cafe.png",
        user_name: form.user_name,
        address: "Ithum A 40 Tower B2nd Floor",
        phone_no: mobile10,
        gst: "07FQPRK1625K1ZZ",
        email: "py36183@gmail.com",
        state: "07-Delhi",
        company_name: "Vapco engineers pvt. ltd noida office 204",
        company_no: "8218667967",
      },
      item: {
        order_id: form.order_id,
        order_date: form.order_date,
        orderType: form.orderType,
        items: form.items.map((it) => ({
          product_id: it.product_id,
          quantity: String(it.quantity || ""),
          item_price: String(it.item_price || ""),
          total_price: String(it.total_price || ""),
        })),
        totalquantity: String(totalQty || ""),
        subtotal: subtotal.toFixed(2),
        Received: received.toFixed(2),
        Balance: balance.toFixed(2),
        totalinwords: form.totalinwords,
        totalOrderPrice: subtotal.toFixed(2),
      },
      bankDetails: {
        upicode: "https://bumppy.in/anlook/api/v1/qrcodes/_A1223_27533.png",
        bank_name: "AXIS BANK",
        account_no: "10147666515",
        ifsc: "IDFB0021331",
        account_holder: "CHAAFO PVT LTD",
      },
    };

    try {
      setLoading(true);
      const res = await generateInvoiceImage(payload);
      const rawUrl =
        res?.data?.image_url ||
        res?.data?.data?.image_url ||
        "";

      if (!rawUrl) {
        setPreview("");
        toast.error("Failed to generate invoice image");
        return;
      }

      const finalUrl = rawUrl.includes("?")
        ? `${rawUrl}&_=${Date.now()}`
        : `${rawUrl}?_=${Date.now()}`;
      setPreview(finalUrl);
      toast.success("Invoice image generated");
    } catch (err) {
      console.error("generateInvoiceImage error", err);
      setPreview("");
      toast.error("Error generating invoice image");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const totals = computeInvoiceTotals(form);

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div
        className="admin-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "96%",
          maxWidth: 900,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        <div className="admin-modal-header">
          <h4>Create Invoice (Image)</h4>
          <button className="admin-icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div
          className="admin-drawer-body admin-drawer-form"
          style={{ overflowY: "auto" }}
        >
          {/* Customer details */}
          <div className="row">
            <div className="col-md-6">
              <div className="admin-field">
                <label>Customer Name (user_name)</label>
                <input
                  className="admin-input-control"
                  value={form.user_name}
                  onChange={(e) =>
                    handleFieldChange("user_name", e.target.value)
                  }
                  placeholder="Customer name"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="admin-field">
                <label>Customer Mobile (phone_no)</label>
                <input
                  className="admin-input-control"
                  value={form.phone_no}
                  onChange={(e) =>
                    handleFieldChange(
                      "phone_no",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  placeholder="10-digit mobile"
                />
              </div>
            </div>
          </div>

          {/* Order meta */}
          <div className="row">
            <div className="col-md-4">
              <div className="admin-field">
                <label>Order ID</label>
                <input
                  className="admin-input-control"
                  value={form.order_id}
                  onChange={(e) =>
                    handleFieldChange("order_id", e.target.value)
                  }
                  placeholder="Optional order id"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="admin-field">
                <label>Order Date</label>
                <input
                  type="date"
                  className="admin-input-control"
                  value={form.order_date}
                  onChange={(e) =>
                    handleFieldChange("order_date", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="admin-field">
                <label>Order Type</label>
                <input
                  className="admin-input-control"
                  value={form.orderType}
                  onChange={(e) =>
                    handleFieldChange("orderType", e.target.value)
                  }
                  placeholder="e.g. Dine-in / Delivery"
                />
              </div>
            </div>
          </div>

          {/* Items table */}
          <div className="admin-field">
            <label>Items</label>
            <div style={{ overflowX: "auto" }}>
              <table className="admin-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ width: "35%" }}>Product ID / Name</th>
                    <th style={{ width: "15%" }}>Qty</th>
                    <th style={{ width: "20%" }}>Item Price</th>
                    <th style={{ width: "20%" }}>Total Price</th>
                    <th style={{ width: "10%" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          className="admin-input-control"
                          value={it.product_id}
                          onChange={(e) =>
                            handleItemChange(idx, "product_id", e.target.value)
                          }
                          placeholder="Item name / code"
                        />
                      </td>
                      <td>
                        <input
                          className="admin-input-control"
                          inputMode="decimal"
                          value={it.quantity}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "quantity",
                              e.target.value
                                .replace(/[^0-9.]/g, "")
                                .replace(/(\..*)\./g, "$1")
                            )
                          }
                          placeholder="Qty"
                        />
                      </td>
                      <td>
                        <input
                          className="admin-input-control"
                          inputMode="decimal"
                          value={it.item_price}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "item_price",
                              e.target.value
                                .replace(/[^0-9.]/g, "")
                                .replace(/(\..*)\./g, "$1")
                            )
                          }
                          placeholder="Rate"
                        />
                      </td>
                      <td>
                        <input
                          className="admin-input-control"
                          inputMode="decimal"
                          value={it.total_price}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "total_price",
                              e.target.value
                                .replace(/[^0-9.]/g, "")
                                .replace(/(\..*)\./g, "$1")
                            )
                          }
                          placeholder="Total"
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className="admin-icon-btn"
                          onClick={() => removeItem(idx)}
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="admin-btn admin-btn-neutral btn--sm"
                onClick={addItem}
              >
                <Plus size={16} style={{ marginRight: 4 }} /> Add Item
              </button>
            </div>
          </div>

          {/* Amounts */}
          <div className="row">
            <div className="col-md-4">
              <div className="admin-field">
                <label>Received (₹)</label>
                <input
                  className="admin-input-control"
                  inputMode="decimal"
                  value={form.Received}
                  onChange={(e) =>
                    handleFieldChange(
                      "Received",
                      e.target.value
                        .replace(/[^0-9.]/g, "")
                        .replace(/(\..*)\./g, "$1")
                    )
                  }
                  placeholder="Amount received"
                />
              </div>
            </div>
            <div className="col-md-8">
              <div className="admin-field">
                <label>Total In Words</label>
                <input
                  className="admin-input-control"
                  value={form.totalinwords}
                  onChange={(e) =>
                    handleFieldChange("totalinwords", e.target.value)
                  }
                  placeholder="e.g. One Thousand Five Hundred Only"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="admin-field">
            <label>Summary</label>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <div>Subtotal: <b>₹ {totals.subtotal.toFixed(2)}</b></div>
              <div>Total Qty: <b>{totals.totalQty}</b></div>
              <div>Received: <b>₹ {totals.received.toFixed(2)}</b></div>
              <div>Balance: <b>₹ {totals.balance.toFixed(2)}</b></div>
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="admin-field">
              <label>Invoice Preview</label>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 8,
                  border: "1px solid #eee",
                  borderRadius: 12,
                  background: "#fafafa",
                }}
              >
                <img
                  src={preview}
                  alt="Invoice"
                  style={{
                    width: "100%",
                    maxWidth: 560,
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: 8,
                  }}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <a
                  href={preview}
                  target="_blank"
                  rel="noreferrer"
                  className="admin-link"
                >
                  Open image in new tab
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="admin-modal-actions">
          <button
            type="button"
            className="admin-btn"
            onClick={onClose}
            disabled={loading}
          >
            <X size={16} /> Close
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-success"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? <Loader2 className="admin-spin" size={16} /> : <FileText size={16} />}
            <span style={{ marginLeft: 6 }}>
              {loading ? "Generating..." : "Generate Invoice Image"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
